/**
 * View Manager Hooks
 *
 * Purpose: Custom hooks for managing view data and dialogs in ViewManager
 *
 * Hooks:
 * - useViewData: Manages view list fetching and model loading
 * - useViewDialogs: Manages modal states for create/edit/detail operations
 *
 * Author: Development Team
 * Created: 2025-01-21 (Refactoring Phase 2)
 */

import { useState, useCallback } from 'react';
import metadataService, { View, Model } from '../services/metadataService';

// ============================================================================
// useViewData Hook
// ============================================================================

export interface ViewFilters {
  model_id: string;
}

export interface UseViewDataReturn {
  data: View[];
  models: { group: string; options: Model[] }[];
  loading: boolean;
  error: string | null;
  loadData: () => Promise<void>;
  loadModels: () => Promise<void>;
}

/**
 * Hook for managing view data and model loading
 */
export function useViewData(tenantId?: string): UseViewDataReturn {
  const [data, setData] = useState<View[]>([]);
  const [models, setModels] = useState<{ group: string; options: Model[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load models for filter dropdown
  const loadModels = useCallback(async () => {
    try {
      // Get both normal and fielddef models
      const [normalModels, fielddefModels] = await Promise.all([
        metadataService.getNormalModels(),
        metadataService.getFieldDefinitionModels(),
      ]);

      // Create grouped options for the dropdown
      const groupedModels = [
        {
          group: 'Business Models',
          options: normalModels.sort((a, b) => a.name.localeCompare(b.name)),
        },
        {
          group: 'Field Definition Models',
          options: fielddefModels.sort((a, b) => a.name.localeCompare(b.name)),
        },
      ];

      setModels(groupedModels);
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the actual API with tenant_id to filter views correctly
      // When tenant_id is provided, backend will exclude GLOBAL views that have tenant overrides
      const response = await metadataService.getViews(tenantId);
      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load views');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  return {
    data,
    models,
    loading,
    error,
    loadData,
    loadModels,
  };
}

// ============================================================================
// useViewDialogs Hook
// ============================================================================

export interface UseViewDialogsReturn {
  // Create/Edit Modal
  modalOpen: boolean;
  modalMode: 'create' | 'edit';
  selectedView: View | undefined;
  openCreateModal: () => void;
  openEditModal: (view: View) => void;
  closeModal: () => void;

  // Detail Modal
  detailModalOpen: boolean;
  openDetailModal: (view: View) => void;
  closeDetailModal: () => void;
  switchToEditMode: () => void;
}

/**
 * Hook for managing modal and dialog states
 */
export function useViewDialogs(): UseViewDialogsReturn {
  // Create/Edit Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedView, setSelectedView] = useState<View | undefined>();

  // Detail Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Create/Edit Modal handlers
  const openCreateModal = useCallback(() => {
    setSelectedView(undefined);
    setModalMode('create');
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((view: View) => {
    setSelectedView(view);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Detail Modal handlers
  const openDetailModal = useCallback((view: View) => {
    setSelectedView(view);
    setDetailModalOpen(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setDetailModalOpen(false);
  }, []);

  // Switch from detail modal to edit modal
  const switchToEditMode = useCallback(() => {
    setDetailModalOpen(false);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  return {
    // Create/Edit Modal
    modalOpen,
    modalMode,
    selectedView,
    openCreateModal,
    openEditModal,
    closeModal,

    // Detail Modal
    detailModalOpen,
    openDetailModal,
    closeDetailModal,
    switchToEditMode,
  };
}
